<script lang="ts" setup>
import { ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { VForm } from "vuetify/components";

import OmniDialog from "@/components/omni/OmniDialog.vue";
import OrderExpenseForm from "@/views/orders/view/components/OrderExpenseForm.vue";
import { useOrderExpenses } from "@/composables";

import type { CreateOrderExpense, OrderExpense } from "@/api/orders/dto/order.dto";

const { t } = useI18n();
const { isLoading, createOrderExpense, createOrderExpenseDraft } = useOrderExpenses();

const showDialog = defineModel<boolean>({ default: false });

const props = defineProps<{
  orderId: string;
}>();

const emit = defineEmits<{
  (e: "success", expense: OrderExpense): void;
}>();

const formRef = useTemplateRef<VForm>("form");
const expense = ref<CreateOrderExpense>(createOrderExpenseDraft());

function close() {
  showDialog.value = false;
}

async function addExpense() {
  const validation = await formRef.value?.validate();

  if (!validation?.valid) {
    return;
  }

  const createdExpense = await createOrderExpense(props.orderId, expense.value);

  if (!createdExpense) {
    return;
  }

  emit("success", createdExpense);
  close();
}

watch(showDialog, (value) => {
  if (!value) {
    return;
  }

  expense.value = createOrderExpenseDraft();
});
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    :primary-action-label="t('general.add')"
    :primary-button-loading="isLoading"
    :secondary-action-label="t('general.cancel')"
    :secondary-button-disabled="isLoading"
    :title="t('payments.add-expense')"
    width="800"
    @primary-action:click="addExpense"
    @secondary-action:click="close"
  >
    <v-form ref="form">
      <order-expense-form v-model="expense" />
    </v-form>
  </omni-dialog>
</template>
